"use client";

import { Button, Text, VStack, Box } from "@chakra-ui/react";
import type { LinkTelegramSuccessResponse } from "@/generated/fastapi";
import { FaTelegram } from "react-icons/fa";
import { useEffect, useRef } from "react";
import WithGenericDialog from "@/shared/ui/gestures/WithGenericDialog";

interface TelegramLinkDialogProps {
  isOpen?: boolean;
  onCloseAction: () => void;
  linkData: LinkTelegramSuccessResponse;
}

export default function TelegramLinkDialog({
  isOpen = false,
  onCloseAction,
  linkData,
}: TelegramLinkDialogProps) {
  // Parse props
  const { link_url: linkUrl, token, expires_in: expiresIn } = linkData;

  // Create a ref to the link element
  const telegramLinkRef = useRef<HTMLAnchorElement>(null);

  // Add a global keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Ctrl+Enter to open Telegram
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        // Programmatically click the link
        telegramLinkRef.current?.click();
      }
    };

    // Add the event listener to the document
    document.addEventListener("keydown", handleKeyDown);

    // Clean up
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, linkUrl]);

  return (
    <WithGenericDialog
      title="Link Telegram Account"
      isOpen={isOpen}
      onCloseAction={onCloseAction}
      onOpenChangeAction={(open) => {
        if (!open) {
          onCloseAction();
        }
      }}
    >
      <VStack spaceY={4} align="stretch">
        <Text>Click the button below to open Telegram and connect your account:</Text>

        <a
          ref={telegramLinkRef}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            size="lg"
            height="12"
            width="full"
            bg="moveon.primary"
            onKeyDown={(e) => {
              if (!isOpen) return;
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                telegramLinkRef.current?.click();
              }
            }}
          >
            <FaTelegram />
            Open Telegram
          </Button>
        </a>

        <Box
          p="3"
          bg="moveon.surface.subtle"
          borderColor="moveon.surface.border"
          borderWidth="1px"
          borderRadius="md"
          fontSize="sm"
        >
          <Text fontWeight="bold" mb="1">
            Your verification token:
          </Text>
          <Text
            p="2"
            bg="moveon.surface.bg"
            borderColor="moveon.surface.border"
            borderWidth="1px"
            borderRadius="md"
            userSelect="all"
          >
            {token}
          </Text>
          {expiresIn && (
            <Text fontSize="xs" color="moveon.text.muted" mt="1">
              This token will expire in {Math.floor(expiresIn / 60)} minutes.
            </Text>
          )}
          <Text mt="2" fontSize="xs" color="moveon.text.muted">
            After opening Telegram, send this token to the bot to verify your
            account.
          </Text>
        </Box>
      </VStack>
    </WithGenericDialog>
  );
}
